import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { SHARE_DAYS, type DocumentDto, type DocumentShareDto } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconCopy } from "../../app/icons";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { errMsg } from "./DossierDialogs";
import { fmtDate } from "./useDossier";
import { DirectorySuggestions } from "../directory/Pick";

const sharesKey = (docId: string) => ["document-shares", docId] as const;

const DAY_LABELS: Record<(typeof SHARE_DAYS)[number], string> = {
  1: "1 dag",
  7: "1 week",
  30: "30 dagen",
  90: "90 dagen",
};

const shareState = (s: DocumentShareDto) =>
  s.revokedAt ? "Ingetrokken" : new Date(s.expiresAt) < new Date() ? "Verlopen" : `Tot ${fmtDate(s.expiresAt)}`;

/**
 * Deellink (docs/DOSSIER.md): een externe (aannemer, verzekeraar, auditor) opent één document zonder account.
 * De link verloopt vanzelf, kan worden ingetrokken en telt hoe vaak hij is geopend.
 */
export function ShareDialog({ doc, onClose }: { doc: DocumentDto; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const shares = useQuery({
    queryKey: sharesKey(doc.id),
    queryFn: () => api<DocumentShareDto[]>(`/documents/${doc.id}/shares`),
  });
  const [days, setDays] = useState<(typeof SHARE_DAYS)[number]>(7);
  const [note, setNote] = useState("");
  const [fixedVersion, setFixedVersion] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ token: string; share: DocumentShareDto }>(`/documents/${doc.id}/shares`, {
        method: "POST",
        body: { days, note: note.trim() || null, fixedVersion },
      });
      setLink(`${location.origin}/d/${res.token}`);
      setNote("");
      await qc.invalidateQueries({ queryKey: sharesKey(doc.id) });
      void qc.invalidateQueries({ queryKey: ["logbook", "document", doc.id] });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast("Link gekopieerd");
    } catch {
      toast("Kopiëren lukt niet; selecteer de link en kopieer hem zelf");
    }
  };
  const revoke = async (s: DocumentShareDto) => {
    if (!window.confirm("Deze link intrekken? Wie hem heeft, kan het document dan niet meer openen.")) return;
    try {
      const list = await api<DocumentShareDto[]>(`/document-shares/${s.id}`, { method: "DELETE" });
      qc.setQueryData(sharesKey(doc.id), list);
      void qc.invalidateQueries({ queryKey: ["logbook", "document", doc.id] });
      toast("Link ingetrokken");
    } catch (err) {
      toast(errMsg(err));
    }
  };

  return (
    <Modal
      title="Delen"
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Sluiten
          </button>
          <button type="button" className="btn primary" onClick={() => void create()} disabled={busy}>
            Link maken
          </button>
        </>
      }
    >
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <p className="muted small">
        Iemand zonder account kan met de link <strong>{doc.title}</strong> bekijken en downloaden. Alleen dit document,
        niets anders uit het dossier.
      </p>
      {link && (
        <div className="doc-sharelink panel" role="status">
          <label className="field">
            <span className="label">Nieuwe link</span>
            <input readOnly value={link} onFocus={(e) => e.target.select()} />
          </label>
          <button type="button" className="btn small" onClick={() => void copy()}>
            <IconCopy size={16} aria-hidden="true" /> Kopiëren
          </button>
          <span className="hint">Kopieer de link nu; hij wordt niet opnieuw getoond.</span>
        </div>
      )}
      <div className="grid2">
        <label className="field">
          <span className="label">Geldig</span>
          <select value={days} onChange={(e) => setDays(Number(e.target.value) as (typeof SHARE_DAYS)[number])}>
            {SHARE_DAYS.map((d) => (
              <option key={d} value={d}>
                {DAY_LABELS[d]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Voor wie</span>
          <input
            value={note}
            maxLength={120}
            onChange={(e) => setNote(e.target.value)}
            placeholder="bijv. Installatiebedrijf Jansen"
            list="share-note-suggestions"
          />
          <DirectorySuggestions id="share-note-suggestions" query={{ kind: undefined, buildingId: doc.buildingId }} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={fixedVersion} onChange={(e) => setFixedVersion(e.target.checked)} /> Alleen
        versie {doc.current.version} (anders opent de link steeds de nieuwste versie)
      </label>
      {shares.data && shares.data.length > 0 && (
        <>
          <h3 className="doc-sharehead">Links</h3>
          <ul className="dir-items" aria-label="Deellinks">
            {shares.data.map((s) => {
              const active = !s.revokedAt && new Date(s.expiresAt) >= new Date();
              return (
                <li key={s.id} className={active ? "" : "muted"}>
                  <span className="grow">
                    <strong>{s.note ?? "Deellink"}</strong>
                    {s.version && <span className="muted small"> · versie {s.version}</span>}
                    <span className="muted small block">
                      {shareState(s)} · {s.openCount}× geopend
                      {s.lastOpenedAt && `, laatst ${fmtDate(s.lastOpenedAt)}`}
                      {s.createdBy && ` · ${s.createdBy}`}
                    </span>
                  </span>
                  {active && (
                    <button type="button" className="btn small ghost danger" onClick={() => void revoke(s)}>
                      Intrekken
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Modal>
  );
}
